import sanitizeHTML from 'sanitize-html'

import Logger from 'src/core/utils/Logger'
import { isTokenExpired } from 'src/core/utils/Token'

import { guid } from '../helpers/GuidHelper'
import { getTokens } from '../helpers/StorageHelper'
import { authService } from '../services/AuthService'

import { EventEmitter } from './EventEmitter'
import { Ready } from './Ready'

interface TRequest {
  resolve: (value: any) => void
  reject: (value: Error) => void
  header: Record<string, any>
  payload: Record<string, any>
  returnError: boolean
  date: number
}

export interface TResponse<T extends Record<string, any>> {
  status: 'success' | 'error' | 'fail'
  data: T
  message?: string
}

interface ICallArgs {
  service: string
  action: string
  token?: string
  payload?: Record<string, any>
  returnError?: boolean
  withoutTokenRefresh?: boolean
  isAuth?: boolean
}

export class WebSocketClient extends EventEmitter {
  public isConnected = false
  public ready = new Ready()
  protected reAuthUUID = ''
  protected noTokenRefreshUUID = ''
  private wsUrl: string = ''
  private ws?: WebSocket
  private delayAttempt?: number
  private requests: { [key: string]: TRequest } = {}
  private authSecured = false
  /**
   */
  public init(wsUrl: string) {
    this.wsUrl = wsUrl
    this.connect()
    return this.ready.promise
  }

  /**
   */
  public connect() {
    Logger.info('WsApi.connect', this.wsUrl)

    if (this.wsUrl) {
      this.ws = new WebSocket(this.wsUrl)
      this.ws.addEventListener('open', this.handleOpen)
      this.ws.addEventListener('close', this.handleClose)
      this.ws.addEventListener('error', this.handleError)
      this.ws.addEventListener('message', this.handleMessage)
    }
  }

  /**
   */
  public async call<T extends Record<string, any> = Record<string, any>>({
    service,
    action,
    payload = {},
    token,
    returnError = false,
    withoutTokenRefresh = false,
    isAuth = false,
  }: ICallArgs): Promise<TResponse<T>> {
    if (isAuth && !this.authSecured) {
      await this.setAuthConnection()
      this.authSecured = true
    }
    const uuid = guid()
    if (withoutTokenRefresh) {
      this.noTokenRefreshUUID = uuid
    }
    const header = { uuid, service, action }
    this.send(header, payload)
    return new Promise((resolve, reject) => {
      this.requests[uuid] = {
        resolve,
        reject,
        header,
        payload,
        returnError,
        date: Date.now(),
      }
    })
  }

  /**
   */
  /**
   * @private
   * @param {Object} header
   * @param {Object} payload
   */
  private async send(header: Record<string, any>, payload: Record<string, any> = {}) {
    const message = JSON.stringify({ header, payload })
    if (header.uuid === this.reAuthUUID) {
      this.ws && this.ws.send(message)
    } else {
      await this.ready.promise
      this.ws && this.ws.send(message)
    }
  }

  /**
   */
  private handleOpen = () => {
    Logger.debug('WsApi.handleOpen', this.wsUrl)
    this.isConnected = true
    this.ready.resolve()
    this.fire('open')
    this.delayAttempt = 0
  }

  /**
   */
  private handleClose = () => {
    Logger.debug('WsApi.handleClose')
    this.close()

    Logger.warn('WsApi Reconnect')
    if (this.delayAttempt) {
      setTimeout(() => this.connect(), this.delayAttempt)
    } else {
      this.delayAttempt = 1000
      this.connect()
    }
  }

  private close() {
    this.ready = new Ready()
    this.isConnected = false
    this.authSecured = false
    this.fire('close')
    this.clearEvents()
  }

  /**
   */
  private handleError = (e: any) => {
    this.fire('error')
    Logger.error('WsApi.handleError', e)
  }

  /**
   */
  private handleMessage = async (e: any) => {
    try {
      const data = JSON.parse(e.data) || {}
      if (data.header) {
        const uuid = data.header.uuid
        const payload = data.payload || {}
        const status = payload.status
        const isError = ['error', 'fail'].includes(status)
        const error = this.getErrorMessage(payload, isError)
        const returnError = this.requests[uuid] && this.requests[uuid].returnError

        if (uuid === this.reAuthUUID) {
          if (isError) {
            this.close()
            authService.clearToken()
            window.location.replace('/login')
          } else {
            Logger.info('Refreshed')
            this.ready.resolve()
          }
        } else if (isError && !returnError) {
          Logger.error('Api.handleMessage error', {
            request: {
              header: this.requests[uuid].header,
              payload: this.requests[uuid].payload,
              date: this.requests[uuid].date,
            },
            response: { payload, header: data.header, date: Date.now() },
          })

          if (
            payload &&
            payload.status === 'fail' &&
            payload.code === 101 &&
            uuid !== this.noTokenRefreshUUID
          ) {
            const result = await this.setAuthConnection()
            const headerService = this.requests[uuid].header['service']
            const headerAction = this.requests[uuid].header['action']

            if (result) {
              data.payload = await this.repeatFailRequest(
                headerService,
                headerAction,
                this.requests[uuid].payload
              )
              this.resolveRequest(data)
            }
          }

          if (this.requests[uuid]) {
            returnError && this.requests[uuid].header.type === 'response'
              ? this.resolveRequest(data)
              : this.requests[uuid].reject(error)
            delete this.requests[uuid]
          }
        } else if (data.header.type === 'event') {
          const eventName = data.header.name
            ? data.header.name
            : `${data.header.service}:${data.header.action}`
          this.log(`Api event: ${eventName}`, { payload })
          this.fire(eventName, payload)
        } else if (data.header.type === 'response' && this.requests[uuid]) {
          this.resolveRequest(data)
        }
      } else {
        Logger.error('Api.handleMessage incorrect response', sanitizeHTML(data))
      }
    } catch (error) {
      Logger.error(error)
    }
  }

  private getErrorMessage = (payload: any, isError: boolean) => {
    if (!isError) {
      return null
    }

    if (payload.data) {
      // validation array
      if (payload.data instanceof Array && payload.data[0] && payload.data[0].message) {
        return payload.data[0].message
      }
      if (payload.data.message) {
        return payload.data.message
      }
    }

    if (payload.message) {
      return payload.message
    }

    return 'Error'
  }

  private async setAuthConnection() {
    Logger.info('Set Auth Connection')
    let [accessToken] = getTokens()
    if (!accessToken || isTokenExpired(accessToken)) {
      await authService.refreshToken()
      accessToken = getTokens()[0]
    }
    if (accessToken) {
      const authPayload = { token: accessToken }
      this.reAuthUUID = guid()
      const header = {
        uuid: this.reAuthUUID,
        service: 'auth',
        action: 'token',
      }
      const message = JSON.stringify({ header, payload: authPayload })
      await this.ready.promise
      this.ws && this.ws.send(message)
      this.ready = new Ready()
      return true
    }

    return false
  }

  private async repeatFailRequest(service: string, action: string, payload: Record<string, any>) {
    return this.call({
      service,
      action,
      payload,
      returnError: false,
      withoutTokenRefresh: true,
    })
  }

  /**
   */
  private resolveRequest = (data: Record<string, any>) => {
    const {
      payload = {},
      header: { uuid },
    } = data

    const key = `${data.header.service}:${data.header.action}`
    this.log(`${new Date().toISOString()} Api call: ${key}`, {
      request: {
        header: this.requests[uuid].header,
        payload: this.requests[uuid].payload,
        date: this.requests[uuid].date,
      },
      response: { payload, header: data.header, date: Date.now() },
    })

    if (!payload.data) {
      payload.data = {}
    }
    this.requests[uuid].resolve(payload)
    delete this.requests[uuid]
  }

  /**
   */
  private log = (groupName: string, data: any) => {
    Logger.groupCollapsed(groupName)
    Object.keys(data).forEach((k) => Logger.info(k, JSON.parse(JSON.stringify(data[k]))))
    try {
      Logger.info(`${data.response.date - data.request.date} ms`)
    } catch {}
    Logger.groupEnd()
  }

  /**
   */
  private clearEvents = () => {
    if (this.ws) {
      this.ws.removeEventListener('open', this.handleOpen)
      this.ws.removeEventListener('close', this.handleClose)
      this.ws.removeEventListener('error', this.handleError)
      this.ws.removeEventListener('message', this.handleMessage)
    }
  }
}

const webSocketClient = new WebSocketClient()
export default webSocketClient
//Check SCM Triggers
